import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Eye, Lock, Volume2, Play, Crosshair } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EXPERTISE_IMG =
  "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjaW5lbWF0b2dyYXBoZXIlMjBmaWxtJTIwc2V0fGVufDB8fHx8MTc4ODAzMDI1Nnww&ixlib=rb-4.1.0&q=85";
const PRACTICAL_IMG =
  "https://images.unsplash.com/photo-1611784728558-6c7d9b409cdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxjaW5lbWF0b2dyYXBoZXIlMjBmaWxtJTIwc2V0fGVufDB8fHx8MTc4ODAzMDI1Nnww&ixlib=rb-4.1.0&q=85";
const PORTFOLIO_IMG =
  "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGVkaXRpbmclMjB0aW1lbGluZXxlbnwwfHx8fDE3ODgwMzAyNTZ8MA&ixlib=rb-4.1.0&q=85";

const VID_EXPERTISE = "https://videos.pexels.com/video-files/14367163/14367163-hd_1920_1080_30fps.mp4";
const VID_PRACTICAL = "https://videos.pexels.com/video-files/8089122/8089122-sd_960_506_25fps.mp4";
const VID_MASTER = "https://videos.pexels.com/video-files/36604691/15520313_640_360_25fps.mp4";

const pad = (n) => String(n).padStart(2, "0");
function toTimecode(p) {
  const total = Math.floor(Math.min(Math.max(p, 0), 1) * 24 * 12);
  return `00:${pad(Math.floor(total / (24 * 60)))}:${pad(Math.floor(total / 24) % 60)}:${pad(total % 24)}`;
}

const SCENES = [
  { until: 0.18, label: "SCENE 01 — THE BLANK PAGE" },
  { until: 0.36, label: "SCENE 02 — THE EXPERTISE" },
  { until: 0.54, label: "SCENE 03 — LEARN BY DOING" },
  { until: 0.72, label: "SCENE 04 — YOUR PORTFOLIO" },
  { until: 1.01, label: "FINAL — MASTER" },
];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Corner({ className }) {
  return <span className={`absolute w-5 h-5 border-white/70 ${className}`} aria-hidden="true" />;
}

const WAVEFORM = [4, 8, 5, 11, 7, 13, 9, 6, 14, 10, 5, 8, 12, 7, 15, 9, 4, 11, 6, 13, 8, 5, 12, 10, 7, 14, 6, 9, 4, 12, 8, 11, 5, 13, 7, 10, 6, 14, 9, 5, 11, 8, 13, 4, 10, 7, 12, 6, 15, 9, 5, 11, 8, 13, 7, 4, 12, 10, 6, 14, 9, 5, 11, 7, 13, 8, 4, 10, 12, 6, 9, 14, 5, 8, 11, 7, 13, 6, 10, 4, 12, 9, 15, 7, 5, 11, 8, 13, 6, 10, 14, 4, 9, 12, 7, 5, 11, 8, 6, 13, 10, 4, 12, 7, 9, 14, 5, 11, 8, 13, 6, 10, 7, 12, 4, 9];

const RULER_MARKS = ["00:00:00:00", "00:00:03:00", "00:00:06:00", "00:00:09:00", "00:00:12:00"];

const CLIPS = [
  { name: "SCRIPT.MOV", left: "0.4%", width: "19.2%" },
  { name: "EXPERTISE_10YRS.MOV", left: "20.4%", width: "19.2%" },
  { name: "PRACTICAL_SET.MOV", left: "40.4%", width: "19.2%" },
  { name: "PORTFOLIO_BUILD.MOV", left: "60.4%", width: "19.2%" },
  { name: "MASTER.MOV", left: "80.4%", width: "19.2%" },
];

function PremiereTimeline({ playheadLeft }) {
  return (
    <div className="absolute bottom-0 left-0 right-0 z-40 border-t border-black bg-[#1b1b1b]" data-testid="hero-timeline">
      <div className="flex h-[110px] md:h-[126px]">
        <div className="w-14 md:w-24 shrink-0 border-r border-black flex flex-col select-none">
          <div className="h-6 border-b border-black bg-[#232323] flex items-center px-1.5">
            <span className="font-mono2 text-[8px] md:text-[9px] text-white/40 tracking-wider">SEQUENCE 01</span>
          </div>
          <div className="flex-1 border-b border-black bg-[#232323] flex items-center justify-between px-1.5 md:px-2.5">
            <span className="font-mono2 text-[10px] md:text-xs text-white/75">V1</span>
            <span className="hidden md:flex items-center gap-2 text-white/35">
              <Eye size={11} /> <Lock size={11} />
            </span>
          </div>
          <div className="h-9 bg-[#232323] flex items-center justify-between px-1.5 md:px-2.5">
            <span className="font-mono2 text-[10px] md:text-xs text-white/75">A1</span>
            <Volume2 size={11} className="text-white/35 hidden md:block" />
          </div>
        </div>

        <div className="relative flex-1 flex flex-col">
          <div className="relative h-6 border-b border-black bg-[#1f1f1f]">
            <div className="absolute inset-0 flex justify-between items-end px-[1px]" aria-hidden="true">
              {Array.from({ length: 61 }).map((_, i) => (
                <span key={i} className={`w-px bg-white/25 ${i % 12 === 0 ? "h-2.5" : i % 3 === 0 ? "h-1.5" : "h-1"}`} />
              ))}
            </div>
            <div className="absolute inset-0 flex justify-between px-1 items-start pt-0.5" aria-hidden="true">
              {RULER_MARKS.map((m) => (
                <span key={m} className="font-mono2 text-[7px] md:text-[9px] text-white/45">{m}</span>
              ))}
            </div>
          </div>

          <div className="relative flex-1 border-b border-black bg-[#161616]">
            <div className="absolute left-0 right-0 top-[2px] h-[2px] bg-lima-red/70" aria-hidden="true" />
            {CLIPS.map((c) => (
              <div
                key={c.name}
                className="absolute top-[7px] bottom-[4px] rounded-[3px] bg-[#3f5c7a] border border-[#27405a] overflow-hidden"
                style={{ left: c.left, width: c.width }}
                aria-hidden="true"
              >
                <div className="h-3.5 bg-[#35506b] flex items-center px-1.5">
                  <span className="font-mono2 text-[7px] md:text-[9px] text-white/85 truncate">{c.name}</span>
                </div>
                <div className="flex gap-[2px] px-1 pt-1 opacity-40">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <span key={i} className="flex-1 h-3 md:h-5 bg-[#7ea6c8] rounded-[1px]" />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="relative h-9 bg-[#161616]">
            <div className="absolute left-[0.4%] right-[0.4%] top-[4px] bottom-[4px] rounded-[3px] bg-[#2f6b45] border border-[#1f4a2f] overflow-hidden" aria-hidden="true">
              <div className="h-3.5 bg-[#285a3b] flex items-center px-1.5">
                <span className="font-mono2 text-[7px] md:text-[9px] text-white/85">MIX_FINAL.WAV</span>
              </div>
              <div className="flex items-center gap-[1px] px-1 h-3.5">
                {WAVEFORM.map((h, i) => (
                  <span key={i} className="flex-1 bg-[#8fd6a8]/70 rounded-[1px]" style={{ height: `${h}px`, maxHeight: "100%" }} />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            style={{ left: playheadLeft }}
            className="absolute top-0 bottom-0 w-[2px] bg-lima-red z-10"
            data-testid="hero-playhead"
          >
            <span className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[9px] border-l-transparent border-r-transparent border-t-lima-red" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function SceneImage({ src, video, alt, opacity, scale, focus, children, overlay = "bg-black/50" }) {
  const mediaCls = "absolute inset-0 w-full h-full object-cover saturate-[.65] contrast-[1.05]";
  const forcePlay = (el) => {
    if (el) el.play().catch(() => {});
  };
  return (
    <motion.div style={{ opacity }} className="absolute inset-0 z-20">
      {video ? (
        <motion.video
          ref={forcePlay}
          src={video}
          poster={src}
          autoPlay
          muted
          loop
          playsInline
          style={{ scale, filter: focus }}
          className={mediaCls}
        />
      ) : (
        <motion.img src={src} alt={alt} style={{ scale, filter: focus }} className={mediaCls} />
      )}
      <div className={`absolute inset-0 ${overlay}`} />
      <Corner className="top-24 left-6 md:left-12 border-t-2 border-l-2" />
      <Corner className="top-24 right-6 md:right-12 border-t-2 border-r-2" />
      <Corner className="bottom-36 left-6 md:left-12 border-b-2 border-l-2" />
      <Corner className="bottom-36 right-6 md:right-12 border-b-2 border-r-2" />
      <motion.div style={{ filter: focus }} className="absolute bottom-44 left-5 md:left-10 max-w-2xl">
        {children}
      </motion.div>
    </motion.div>
  );
}

function FocusTargetHud() {
  return (
    <div className="absolute right-8 md:right-24 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-4 select-none" aria-hidden="true">
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 1, 0.5] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        className="text-lima-blue"
      >
        <Crosshair size={56} strokeWidth={1} />
      </motion.div>
      <div className="text-center font-mono2 text-[9px] tracking-[0.3em] text-white/40 space-y-1.5">
        <p className="text-lima-blue">FOCUS PULL</p>
        <p>T1.8 · ISO 800</p>
        <p>24.976 FPS</p>
      </div>
    </div>
  );
}

export default function ScrollScrubHero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [tc, setTc] = useState("00:00:00:00");
  const [label, setLabel] = useState(SCENES[0].label);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setTc(toTimecode(v));
    setLabel(SCENES.find((s) => v < s.until).label);
  });

  const playheadLeft = useTransform(scrollYProgress, [0, 1], ["1%", "99%"]);

  const s1o = useTransform(scrollYProgress, [0, 0.13, 0.19], [1, 1, 0]);
  const s1y = useTransform(scrollYProgress, [0, 0.19], [0, -70]);
  const s1blur = useTransform(scrollYProgress, [0, 0.13, 0.19], ["blur(0px)", "blur(0px)", "blur(16px)"]);

  const s2o = useTransform(scrollYProgress, [0.16, 0.22, 0.31, 0.37], [0, 1, 1, 0]);
  const s2scale = useTransform(scrollYProgress, [0.16, 0.37], [1.25, 1]);
  const s2blur = useTransform(scrollYProgress, [0.15, 0.24, 0.31, 0.37], ["blur(20px)", "blur(0px)", "blur(0px)", "blur(14px)"]);

  const s3o = useTransform(scrollYProgress, [0.34, 0.4, 0.49, 0.55], [0, 1, 1, 0]);
  const s3scale = useTransform(scrollYProgress, [0.34, 0.55], [1.25, 1]);
  const s3blur = useTransform(scrollYProgress, [0.33, 0.42, 0.49, 0.55], ["blur(20px)", "blur(0px)", "blur(0px)", "blur(14px)"]);

  const s4o = useTransform(scrollYProgress, [0.52, 0.58, 0.67, 0.73], [0, 1, 1, 0]);
  const s4scale = useTransform(scrollYProgress, [0.52, 0.73], [1.2, 1]);
  const s4blur = useTransform(scrollYProgress, [0.51, 0.6, 0.67, 0.73], ["blur(20px)", "blur(0px)", "blur(0px)", "blur(14px)"]);

  const s5o = useTransform(scrollYProgress, [0.7, 0.8], [0, 1]);
  const s5y = useTransform(scrollYProgress, [0.7, 0.94], [90, 0]);
  const s5blur = useTransform(scrollYProgress, [0.68, 0.84], ["blur(22px)", "blur(0px)"]);

  const hintO = useTransform(scrollYProgress, [0, 0.08], [1, 0]);

  if (reduced) {
    return (
      <section className="relative min-h-screen bg-lima-ink flex items-end pt-16" data-testid="hero-section">
        <img src={EXPERTISE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 px-5 md:px-10 pb-24">
          <p className="font-mono2 text-xs tracking-[0.25em] text-lima-red mb-6">LIFE INSIDE MEDIA ACADEMY</p>
          <h1 className="font-display font-bold text-5xl md:text-7xl tracking-tight text-lima-paper max-w-4xl">
            Master the art of <span className="font-serifit font-normal text-lima-blue">filmmaking.</span>
          </h1>
        </div>
      </section>
    );
  }

  return (
    <div ref={ref} className="relative h-[340vh] md:h-[520vh] bg-lima-ink" data-testid="hero-section">
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Top HUD */}
        <div className="absolute top-16 left-0 right-0 z-30 flex items-center justify-between px-5 md:px-10 pt-4">
          <span className="font-mono2 text-[11px] md:text-sm tracking-[0.25em] text-white/60" data-testid="hero-scene-label">
            {label}
          </span>
          <span className="font-mono2 text-[11px] md:text-sm tracking-[0.25em] text-white/80 flex items-center gap-3">
            <span className="hidden md:inline text-white/40">4K&nbsp;·&nbsp;25 FPS</span>
            <span className="w-2 h-2 rounded-full bg-lima-red animate-rec" />
            <span data-testid="hero-timecode">{tc}</span>
          </span>
        </div>

        {/* SCENE 01 — THE BLANK PAGE */}
        <motion.div style={{ opacity: s1o, y: s1y, filter: s1blur }} className="absolute inset-0 z-10">
          <FocusTargetHud />
          <div className="h-full flex flex-col justify-center px-5 md:px-10 max-w-6xl">
            <MaskedLine delay={0.2}>
              <span className="font-mono2 text-[11px] md:text-xs tracking-[0.3em] text-lima-red hidden md:inline">[ SCENE 01 — THE BLANK PAGE ]</span>
            </MaskedLine>
            <div className="mt-8 md:mt-10 space-y-2.5">
              <MaskedLine delay={0.35} className="font-mono2 text-xs md:text-sm text-white/85 tracking-wide">
                INT. EDIT SUITE — NIGHT
              </MaskedLine>
              <MaskedLine delay={0.5} className="font-mono2 text-xs md:text-sm text-white/45 tracking-wide">
                A blank timeline. A cursor blinking.
                <span className="inline-block w-2 h-4 bg-lima-blue align-middle ml-1 animate-rec" />
              </MaskedLine>
            </div>
            <MaskedLine delay={0.7} className="mt-10 md:mt-14 font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tight text-lima-paper leading-[1.0]">
              Somewhere between the script
            </MaskedLine>
            <MaskedLine delay={0.82} className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tight text-lima-paper leading-[1.0]">
              and the screen,
            </MaskedLine>
            <MaskedLine delay={1.0} className="font-serifit text-4xl sm:text-5xl lg:text-7xl text-lima-blue leading-[1.08] mt-2">
              a filmmaker is made — not born.
            </MaskedLine>
            <motion.p style={{ opacity: hintO }} className="mt-10 md:mt-14 font-mono2 text-[11px] tracking-[0.25em] text-white/50 flex items-center gap-3">
              SCROLL TO SCRUB THE TIMELINE
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
                <ArrowDown size={14} />
              </motion.span>
            </motion.p>
          </div>
        </motion.div>

        {/* SCENE 02 — EXPERTISE */}
        <SceneImage src={EXPERTISE_IMG} video={VID_EXPERTISE} alt="Film crew working on a soundstage" opacity={s2o} scale={s2scale} focus={s2blur} overlay="bg-black/55">
          <p className="font-mono2 text-xs tracking-[0.3em] text-lima-red mb-3 hidden md:block">[ SCENE 02 — THE EXPERTISE ]</p>
          <p className="font-serifd text-6xl md:text-8xl text-lima-blue leading-none mb-4">10+</p>
          <p className="font-display font-bold text-4xl md:text-6xl tracking-tight text-lima-paper leading-[1.02]">
            years of real video
            <br />
            production behind
            <br />
            <span className="font-serifit font-normal text-lima-blue">every lesson.</span>
          </p>
          <p className="mt-5 max-w-lg text-sm md:text-base text-white/65 leading-relaxed">
            LIMA is built inside Life Inside Media — a Pune production house trusted by national, multinational and
            international brands for over a decade. You learn from people who deliver for a living.
          </p>
        </SceneImage>

        {/* SCENE 03 — PRACTICAL LEARNING */}
        <SceneImage src={PRACTICAL_IMG} video={VID_PRACTICAL} alt="Actors filming a scene on set" opacity={s3o} scale={s3scale} focus={s3blur} overlay="bg-black/45">
          <p className="font-mono2 text-xs tracking-[0.3em] text-lima-red mb-3 hidden md:block">[ SCENE 03 — LEARN BY DOING ]</p>
          <p className="font-display font-bold text-4xl md:text-6xl tracking-tight text-lima-paper leading-[1.02]">
            No sit-and-read theory.
            <br />
            You <span className="font-serifit font-normal text-lima-blue">shoot</span> from day one.
          </p>
          <p className="mt-5 max-w-lg text-sm md:text-base text-white/65 leading-relaxed">
            Real scripts. Cinema-line cameras. Industry-standard edit suites. Live client briefs with real deadlines —
            practical-based learning inside a real production pipeline.
          </p>
        </SceneImage>

        {/* SCENE 04 — PORTFOLIO */}
        <SceneImage src={PORTFOLIO_IMG} alt="Editing timeline in a dark suite" opacity={s4o} scale={s4scale} focus={s4blur} overlay="bg-black/55">
          <p className="font-mono2 text-xs tracking-[0.3em] text-lima-red mb-3 hidden md:block">[ SCENE 04 — YOUR PORTFOLIO ]</p>
          <p className="font-display font-bold text-4xl md:text-6xl tracking-tight text-lima-paper leading-[1.02]">
            Your portfolio builds
            <br />
            <span className="font-serifit font-normal text-lima-blue">while you learn.</span>
          </p>
          <p className="mt-5 max-w-lg text-sm md:text-base text-white/65 leading-relaxed">
            Every project you cut is real client work. You leave LIMA with a finished body of work, a professional
            portfolio website — and proof, not just a certificate.
          </p>
        </SceneImage>

        {/* FINAL — MASTER */}
        <motion.div style={{ opacity: s5o, y: s5y }} className="absolute inset-0 z-30">
          <motion.video
            ref={(el) => el && el.play().catch(() => {})}
            src={VID_MASTER}
            poster={EXPERTISE_IMG}
            autoPlay
            muted
            loop
            playsInline
            style={{ filter: s5blur }}
            className="absolute inset-0 w-full h-full object-cover opacity-25 saturate-[.6]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-lima-ink via-lima-ink/70 to-lima-ink/30" />
          <Corner className="top-24 left-6 md:left-12 border-t-2 border-l-2" />
          <Corner className="top-24 right-6 md:right-12 border-t-2 border-r-2" />
          <Corner className="bottom-36 left-6 md:left-12 border-b-2 border-l-2" />
          <Corner className="bottom-36 right-6 md:right-12 border-b-2 border-r-2" />
          <motion.div style={{ filter: s5blur }} className="relative h-full flex flex-col justify-end px-5 md:px-10 pb-40 md:pb-44">
            <p className="font-mono2 text-[11px] md:text-xs tracking-[0.3em] text-white/70 mb-6 flex items-center gap-2.5">
              <span className="w-2 h-2 rounded-full bg-lima-red animate-rec" /> ADMISSIONS OPEN · PUNE · 2026
            </p>
            <h1 className="font-display font-bold tracking-tight text-lima-paper leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-5xl">
              Master the art of
              <br />
              <span className="font-serifit font-normal text-lima-blue">filmmaking.</span>
            </h1>
            <p className="mt-7 max-w-xl text-white/65 text-sm md:text-base leading-relaxed">
              Learn everything it takes to become a compelling storyteller — with a clear roadmap into filmmaking and
              video production. Industry-ready from day one.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <motion.button
                whileTap={{ scale: 0.96 }}
                data-testid="hero-explore-programs-button"
                onClick={() => scrollToId("#programs")}
                className="bg-lima-blue hover:bg-lima-bluehover text-white font-mono2 text-xs font-bold tracking-[0.2em] px-7 py-4 transition-colors duration-200 flex items-center gap-2"
              >
                EXPLORE PROGRAMS <ArrowUpRight size={15} />
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.96 }}
                data-testid="hero-enquire-button"
                onClick={() => scrollToId("#why")}
                className="flex items-center gap-3 text-white/80 hover:text-white transition-colors duration-200 group"
              >
                <span className="w-12 h-12 rounded-full border border-white/40 grid place-items-center group-hover:border-lima-blue group-hover:bg-lima-blue transition-colors duration-200">
                  <Play size={15} className="fill-current" />
                </span>
                <span className="text-sm">View the experience</span>
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* TIMELINE BAR */}
        <PremiereTimeline playheadLeft={playheadLeft} />
      </div>
    </div>
  );
}
